import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Rnd } from "react-rnd";
import SvgIcon from "../../../../Components/SvgIcon";
import action from "../../../../store/action";
import { useShortcutKeyActions } from "../../../../ShortcutKeys/ShortcutKeyActions";
import { SupportShowShortcutKeyView } from "../../../../config/env";
import { nextPanelZIndex } from "../../../../lib/panelZIndex";
import ContextMenuView from "../ContextMenuView";
import {
  SHORTCUTS_KEY_VIEW_DATA,
  SHORTCUT_KEYS_TAB_LIST,
} from "./ShortcutKeysViewData";
import "./index.less";

const BASE_PANEL_WIDTH = 550;
const BASE_PANEL_HEIGHT = 402;
const MIN_PANEL_SCALE = 0.7;
const MAX_PANEL_SCALE = 5;
const clamp = (value, min, max) => {
  return Math.max(min, Math.min(value, max));
};

const getPanelMaxOffset = (scale, viewportWidth, viewportHeight) => {
  return {
    maxX: Math.max(0, viewportWidth - BASE_PANEL_WIDTH * scale),
    maxY: Math.max(0, viewportHeight - BASE_PANEL_HEIGHT * scale),
  };
};

const formatShortcutKey = (key) => {
  const keyMap = {
    ArrowLeft: "←",
    ArrowRight: "→",
    ArrowUp: "↑",
    ArrowDown: "↓",
  };
  return keyMap[key] || key;
};

const getDefaultPanelFrame = (
  scale = 1,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight
) => {
  const rightOffset = 16;
  const topOffset = 60;
  return {
    rightOffset,
    topOffset,
    scale,
  };
};

const MIN_ACTION_FONT_SIZE = 10;
const MAX_ACTION_FONT_SIZE = 14;

function ShortcutActionName({ text }) {
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(MAX_ACTION_FONT_SIZE);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const computeBestSize = () => {
      let low = MIN_ACTION_FONT_SIZE;
      let high = MAX_ACTION_FONT_SIZE;
      let best = low;

      const isFit = (size) => {
        element.style.fontSize = `${size}px`;
        void element.offsetHeight;
        return (
          element.scrollWidth <= element.clientWidth &&
          element.scrollHeight <= element.clientHeight
        );
      };

      if (isFit(high)) {
        best = high;
      } else {
        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          if (isFit(mid)) {
            best = mid;
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }
      }

      element.style.fontSize = `${best}px`;
      setFontSize(best);
    };

    computeBestSize();

    const handleResize = () => computeBestSize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [text]);

  return (
    <span
      ref={textRef}
      className="shortcutActionName"
      style={{
        fontSize: `${fontSize}px`,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        lineHeight: 1.2,
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '4px 12px',
      }}
      title={text}
    >
      {text}
    </span>
  );
}

function ShortcutKeysView(props) {
  const {
    currentDataTool,
    keyframeNumber,
    setKeyframeNumber,
    timelinePlaying,
    setTimelinePlaying,
    setView,
    gridCheckboxValue,
    setGridCheckboxValue,
    IPRCheckboxValue,
    setIPRCheckboxValue,
    collisionCheckboxValue,
    setCollisionCheckboxValue,
    overlayCheckboxValue,
    setOverlayCheckboxValue,
    gumCheckboxValue,
    setGumCheckboxValue,
    attachmentCheckboxValue,
    setAttachmentCheckboxValue,
    setCurrentClickItemId,
    currentClickItemId,
    currentClickInitItemRef,
    cameraControlRef,
    setUpperOrLower,
    upperOrLower,
    sliderVal,
    setSliderValue,
    isCombine,
    stageList,
    byteStageList,
    isLoading,
  } = props;

  const [activeTab, setActiveTab] = useState("view");
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelZIndex, setPanelZIndex] = useState(() => nextPanelZIndex());
  const [panelFrame, setPanelFrame] = useState(getDefaultPanelFrame);
  const [dragBounds, setDragBounds] = useState("window");
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  const supportShortcutKeyView = SupportShowShortcutKeyView();

  useEffect(() => {
    const onResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const effectiveMinScale = MIN_PANEL_SCALE;
  const effectiveMaxScale = MAX_PANEL_SCALE;

  const panelPosition = useMemo(() => {
    const panelWidth = BASE_PANEL_WIDTH * panelFrame.scale;
    return {
      x: viewport.width - panelWidth - panelFrame.rightOffset,
      y: panelFrame.topOffset,
    };
  }, [panelFrame.rightOffset, panelFrame.scale, panelFrame.topOffset, viewport.width]);

  const applyDragPosition = useCallback(
    (x, y) => {
      const panelWidth = BASE_PANEL_WIDTH * panelFrame.scale;
      setPanelFrame((prev) => ({
        ...prev,
        rightOffset: viewport.width - panelWidth - x,
        topOffset: y,
      }));
    },
    [panelFrame.scale, viewport.width]
  );

  const computeDragBounds = useCallback((nodeRect, position) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    return {
      left: -nodeRect.left + position.x,
      right: clientWidth - (nodeRect.right - position.x),
      top: -nodeRect.top + position.y,
      bottom: clientHeight - (nodeRect.bottom - position.y),
    };
  }, []);

  const updateScaleFrame = useCallback((ref, position) => {
    const nextWidth = parseFloat(ref.style.width);
    const scaledValue = nextWidth / BASE_PANEL_WIDTH;
    const nextScale = Math.min(
      effectiveMaxScale,
      Math.max(effectiveMinScale, scaledValue)
    );
    const { maxY } = getPanelMaxOffset(
      nextScale,
      viewport.width,
      viewport.height
    );
    const panelWidth = BASE_PANEL_WIDTH * nextScale;
    const rightOffset = clamp(
      viewport.width - panelWidth - position.x,
      0,
      viewport.width
    );
    setPanelFrame({
      rightOffset,
      topOffset: clamp(position.y, 0, maxY),
      scale: nextScale,
    });
  }, [effectiveMaxScale, effectiveMinScale, viewport.height, viewport.width]);

  useShortcutKeyActions({
    currentDataTool,
    keyframeNumber,
    setKeyframeNumber,
    timelinePlaying,
    setTimelinePlaying,
    setView,
    gridCheckboxValue,
    setGridCheckboxValue,
    IPRCheckboxValue,
    setIPRCheckboxValue,
    collisionCheckboxValue,
    setCollisionCheckboxValue,
    overlayCheckboxValue,
    setOverlayCheckboxValue,
    gumCheckboxValue,
    setGumCheckboxValue,
    attachmentCheckboxValue,
    setAttachmentCheckboxValue,
    setCurrentClickItemId,
    currentClickItemId,
    currentClickInitItemRef,
    cameraControlRef,
    setUpperOrLower,
    upperOrLower,
    sliderVal,
    setSliderValue,
    isCombine,
    stageList,
    byteStageList,
    enableShortcutKeyFeature: supportShortcutKeyView,
  });

  useEffect(() => {
    if (!supportShortcutKeyView) {
      setPanelVisible(false);
      return undefined;
    }
  }, [supportShortcutKeyView]);

  const openShortcutPanel = useCallback(() => {
    setPanelZIndex(nextPanelZIndex());
    setPanelFrame((prev) => {
      const preservedScale = clamp(prev.scale, effectiveMinScale, effectiveMaxScale);
      return getDefaultPanelFrame(
        preservedScale,
        viewport.width,
        viewport.height
      );
    });
    setPanelVisible(true);
  }, [effectiveMaxScale, effectiveMinScale, viewport.height, viewport.width]);

  const closeShortcutPanel = useCallback(() => {
    setPanelVisible(false);
  }, []);

  const renderKeys = useCallback((keysGroup) => {
    return keysGroup.map((keys, keyIndex) => {
      return (
        <div className={"shortcutKeyGroup"} key={`${keys.join("-")}-${keyIndex}`}>
          {keys.map((key, index) => {
            return (
              <React.Fragment key={`${key}-${index}`}>
                <span className={"shortcutKeyCapsule"}>
                  {formatShortcutKey(key)}
                </span>
                {index < keys.length - 1 && (
                  <span className={"shortcutKeyPlus"}>+</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      );
    });
  }, []);

  if (!supportShortcutKeyView) {
    return null;
  }

  return (
    <>
      <ContextMenuView
        enableTrigger={supportShortcutKeyView}
        triggerSelector={"canvas"}
        shouldOpen={() => !isLoading}
        items={[
          {
            id: "keyboard-shortcuts",
            label: "Keyboard Shortcuts",
            onClick: openShortcutPanel,
            visible: !panelVisible,
          },
        ]}
      />

      {panelVisible && (
        <Rnd
          className={"shortcutKeysPanelRnd"}
          style={{
            zIndex: panelZIndex,
          }}
          bounds={dragBounds}
          dragHandleClassName={"shortcutDragHandle"}
          lockAspectRatio={true}
          enableResizing={{
            bottomRight: true,
          }}
          resizeHandleClasses={{
            bottomRight: "shortcutResizeHandleSe",
          }}
          resizeHandleStyles={{
            bottomRight: {
              width: "14px",
              height: "14px",
              right: "0px",
              bottom: "0px",
              cursor: "nwse-resize",
            },
          }}
          size={{
            width: BASE_PANEL_WIDTH * panelFrame.scale,
            height: BASE_PANEL_HEIGHT * panelFrame.scale,
          }}
          position={panelPosition}
          minWidth={BASE_PANEL_WIDTH * effectiveMinScale}
          minHeight={BASE_PANEL_HEIGHT * effectiveMinScale}
          maxWidth={BASE_PANEL_WIDTH * effectiveMaxScale}
          maxHeight={BASE_PANEL_HEIGHT * effectiveMaxScale}
          onDrag={(event, data) => {
            applyDragPosition(data.x, data.y);
          }}
          onDragStop={(event, data) => {
            applyDragPosition(data.x, data.y);
            setDragBounds("window");
          }}
          onResizeStop={(event, direction, ref, delta, position) => {
            updateScaleFrame(ref, position);
          }}
          onDragStart={(event, data) => {
            setPanelZIndex(nextPanelZIndex());
            if (data?.node?.getBoundingClientRect) {
              const rect = data.node.getBoundingClientRect();
              setDragBounds(computeDragBounds(rect, { x: data.x, y: data.y }));
            } else {
              setDragBounds("window");
            }
          }}
          onResizeStart={() => {
            setPanelZIndex(nextPanelZIndex());
          }}
          onResize={(event, direction, ref, delta, position) => {
            updateScaleFrame(ref, position);
          }}
        >
          <div
            className={"shortcutKeysPanel"}
            style={{
              width: `${BASE_PANEL_WIDTH}px`,
              height: `${BASE_PANEL_HEIGHT}px`,
              transform: `scale(${panelFrame.scale})`,
              transformOrigin: "top left",
            }}
          >
            <div className={"shortcutHeader shortcutDragHandle"}>
              <span>Shortcuts</span>
              <button aria-label={"Close"} onClick={closeShortcutPanel}>
                <SvgIcon iconClass={"close"} style={{ fontSize: "10px" }} />
              </button>
            </div>
            <div className={"shortcutTabs"}>
              {SHORTCUT_KEYS_TAB_LIST.map((tab) => {
                return (
                  <button
                    key={tab.key}
                    className={activeTab === tab.key ? "active" : ""}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className={"shortcutTable"}>
              <div className={"shortcutTableHeader"}>
                <span>Action</span>
                <span>Windows</span>
                <span>Mac</span>
              </div>
              <div className={"shortcutTableScrollView"}>
                <div className={"shortcutTableContentPanel"}>
                  {SHORTCUTS_KEY_VIEW_DATA[activeTab].map((row) => {
                    return (
                      <div className={"shortcutTableRow"} key={row.action}>
                        <ShortcutActionName text={row.action} />
                        <div>{renderKeys(row.win)}</div>
                        <div>{renderKeys(row.mac)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <span className={"shortcutResizeIndicator"} aria-hidden="true">
              <SvgIcon iconClass={"Resive"} style={{ width: "10px", height: "10px" }} />
            </span>
          </div>
        </Rnd>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  currentDataTool: state.dataTool.currentDataTool,
  keyframeNumber: state.webViewer.keyframeNumber,
  timelinePlaying: state.webViewer.timelinePlaying,
  gridCheckboxValue: state.webViewer.gridCheckboxValue,
  IPRCheckboxValue: state.webViewer.IPRCheckboxValue,
  collisionCheckboxValue: state.webViewer.collisionCheckboxValue,
  overlayCheckboxValue: state.webViewer.overlayCheckboxValue,
  gumCheckboxValue: state.webViewer.gumCheckboxValue,
  attachmentCheckboxValue: state.webViewer.attachmentCheckboxValue,
  currentClickItemId: state.webViewer.currentClickItemId,
  currentClickInitItemRef: state.webViewer.currentClickInitItemRef,
  cameraControlRef: state.webViewer.cameraControlRef,
  upperOrLower: state.dataTool.upperOrLower,
  isCombine: state.webViewer.isCombine,
  isLoading: state.webViewer.isLoading,
  sliderVal: state.webViewer.sliderVal,
});

const mapDispatchToProps = (dispatch) => ({
  setView: (val) => dispatch(action.setView(val)),
  setGridCheckboxValue: (val) => dispatch(action.setGridCheckboxValue(val)),
  setIPRCheckboxValue: (val) => dispatch(action.setIPRCheckboxValue(val)),
  setTimelinePlaying: (val) => dispatch(action.setTimelinePlaying(val)),
  setCollisionCheckboxValue: (val) =>
    dispatch(action.setCollisionCheckboxValue(val)),
  setOverlayCheckboxValue: (val) =>
    dispatch(action.setOverlayCheckboxValue(val)),
  setGumCheckboxValue: (val) => dispatch(action.setGumCheckboxValue(val)),
  setAttachmentCheckboxValue: (val) =>
    dispatch(action.setAttachmentCheckboxValue(val)),
  setKeyframeNumber: (val) => dispatch(action.setKeyframeNumber(val)),
  setCurrentClickItemId: (val) => dispatch(action.setCurrentClickItemId(val)),
  setUpperOrLower: (val) => dispatch(action.setUpperOrLower(val)),
  setSliderValue: (val) => dispatch(action.setSliderValue(val)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShortcutKeysView);